<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="true"
                :showAddNew="false"
                title="Investigation Result Update"
                @onClickCloseButton="onClickToClose"
            />
        </div>

        <div class="my-2 px-2 mb-3">
            <TableHeaderData 
                :leftSideData="tableHeaderData.leftSideData"
                :rightSideData="tableHeaderData.rightSideData"
                :rightColumnWidth="rightColumnWidth"
                v-model:dischargeDate="dischargeDate"
            >
                <template #left-table-slot>
                    <tr>
                        <td class="table-column" :style="`width: ${rightColumnWidth}`">Report Prepared By</td>
                        <td class="table-divider"></td>
                        <td class="table-row dropdown-consultant"> 
                            <AsyncSelect
                                placeholder="Select Consultant"
                                v-model="preparedBy"
                                :autoSelectFirst="true"
                                :api-service="fetchServiceList"
                                :additional-query="{ resource_type: 'human_resources', user_role: 'pathologist' }"
                                :format-label="option => option.name"
                            />
                        </td>
                    </tr>
                </template>

                <template #right-table-slot>
                  <tr>
                    <td class="table-column" :style="`width: ${rightColumnWidth}`">Delivery Date</td>
                    <td class="table-divider"></td>
                    <td class="table-row  dropdown-consultant">
                      <div class="col-md-7 col-lg-12">
                        <input
                            class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
                            tabindex="1"
                            type="date"
                            v-model="deliveryDate"
                        >
                      </div>
                    </td>
                  </tr>

                    <tr>
                        <td class="table-column" :style="`width: ${rightColumnWidth}`">Report Approved By</td>
                        <td class="table-divider"></td>
                        <td class="table-row  dropdown-consultant"> 
                            <AsyncSelect
                                placeholder="Select Consultant"
                                v-model="approvedBy"
                                :autoSelectFirst="true"
                                :api-service="fetchServiceList"
                                :additional-query="{ resource_type: 'human_resources', user_role: 'pathologist_doctor' }"
                                :format-label="option => option.name"
                            />
                        </td>
                    </tr>
                </template>
            </TableHeaderData>
        </div>

        <InvestigationReport 
            :formattedGenerals="reports.formattedGenerals"
            :antibioticsDrowdown="antibioticsDrowdown"
            v-if="reports && reports.formattedGenerals"
            @selection-change="selectedGenerals = $event"
        />

        <div class="row px-2">
          <div class="col-12 col-md-6 mb-3">
            <label for="attachment" class="form-label fw-bold">Attachment</label>
            <BlobFileViewer :attachments="attachments" />
            <FileUpload
                :is-drag-active="true"
                button-text="Upload Documents"
                v-model="attachments"
            >
            </FileUpload>
          </div>

          <div class="col-12 col-md-6 mb-3">
            <label for="note" class="form-label fw-bold">Remarks</label>
            <textarea
                id="note"
                class="form-control"
                rows="3"
                v-model="note"
            ></textarea>
          </div>
        </div>

        <div class="row px-2">
            <div class="col-12 col-sm-12 col-md-12 col-lg-12 mb-1 text-center">
                <button :disabled="loading" type="button" @click="submit(false)" class="btn btn-primary me-1 waves-effect waves-float waves-light">
                    <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    Save
                </button>
                <button :disabled="loadingPdf" type="button" @click="submit(true)" class="btn btn-primary me-1 waves-effect waves-float waves-light">
                  <div v-if="loadingPdf" class="spinner-border spinner-border-sm text-light" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  Save & Print(With Header)
                </button>
                <button :disabled="loadingPdf" type="button" @click="submit(true, true)" class="btn btn-primary me-1 waves-effect waves-float waves-light">
                    <div v-if="loadingPdf" class="spinner-border spinner-border-sm text-light" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    Save & Print(Without Header)
                </button>
                <button v-if="route.query.is_pathologist === 'true'
                                && route.query.status !== 'approved'" 
                                :disabled="loadingStatus" type="button" 
                                @click="updateStatus" class="btn btn-primary me-1 waves-effect waves-float waves-light">
                    <div v-if="loadingStatus" class="spinner-border spinner-border-sm text-light" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    Approve
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import TableHeaderData from '@/components/molecule/company/hospital/investigation-general/InvestigationGeneralHeader.vue';
import InvestigationReport from '@/components/molecule/company/hospital/investigation-general/InvestigationReport.vue';
import handleHospital from '@/services/modules/hospital'
import handleCompany from "@/services/modules/company";
import { inject, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { getCurrentDateTime, formatDateToDdMmYy, groupBy, formatDate, convertToTitleCase } from '@/services/utils/global';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import FileUpload from "@/components/atom/FileUpload";
import BlobFileViewer from "@/components/atom/BlobFileViewer";
import investigationResultPdfPrinter from '@/services/utils/investigationResultPdfPrinterOld';
import handleBarcode from "@/services/modules/barcode";
import handleInventory from '@/services/modules/inventory';
import { replaceDashWithUnderscore } from '@/services/utils/global';
import useDate from "@/services/utils/day";

const { fetchSaleGeneral, updateInvestigationGeneral, saveInvestigationAttachment, fetchServiceList, updateInvestigationStatus, calculateAge } = handleHospital();
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
const { exportToPDF } = investigationResultPdfPrinter();
const { fetchBarcode } = handleBarcode();
const { fetchHome } = handleInventory();

const store = useStore();
const route = useRoute();
const router = useRouter();
const loading = ref(false);
const loadingPdf = ref(false);
const loadingStatus = ref(false);
const loader = ref(false);
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const tableHeaderData = reactive({
    leftSideData: [],
    rightSideData: [],
});
const rightColumnWidth = '30%';
const dischargeDate = ref(getCurrentDateTime());
const reports = ref(null);
const preparedBy = ref(null);
const approvedBy = ref(null);
const deliveryDate = ref('');
const attachments = ref([]);
const note = ref('');
const companyInfo = ref({});
const barcode = ref('');
const qrcode = ref('');
const barcodePatient = ref('');
const selectedGenerals = ref({});
const antibioticsDrowdown = ref([]);
const customizationPdfFields = ref([]);
const date = useDate();

const submit = async (print=false, isHideHeaderFooter = false) => {
    const formattedData = formatFormData();

    print ? loadingPdf.value = true : loading.value = true;
    await updateInvestigationGeneral(formattedData).then( async (res) => {
        loading.value = false
        if (!res.status) return showError(res.message);
        onClickToClose();
        await saveAttachment();
        if(print) {
            goToPrint(isHideHeaderFooter);
            loadingPdf.value = false;
        }
        return showSuccess(res.message);
    }).catch(err => {
        loading.value = false
    }).finally(() => {
        loading.value = false
        loadingPdf.value = false
    })
}

const saveAttachment = async () => {
    const formData = new FormData();
    let previousFileIds = [];

    formData.append('company_id', route.params.companyId);
    formData.append('report_type', route.query.report_type)
    formData.append('sale_master_id', route.params.saleId);

    for(const key in attachments.value){
        if(typeof attachments.value[key] === 'object' && attachments.value[key].hasOwnProperty('id')) {
            previousFileIds.push(attachments.value[key].id);
            continue;
        }
        formData.append('attachments[]', attachments.value[key]);
    }

    formData.append('previous_file_ids', JSON.stringify(previousFileIds))

    await saveInvestigationAttachment(formData, getQuery());
}

const formatFormData = () => {
    const data = {
        'investigation_results': [],
        'company_id': route.params.companyId,
        'sale_id': route.params.saleId,
        'prepared_by': preparedBy.value ? preparedBy.value.id : '',
        'approved_by': approvedBy.value ? approvedBy.value.id : '',
        'delivery_date': deliveryDate.value ? deliveryDate.value : '',
        'note': note.value,
    }

    Object.values(reports.value.formattedGenerals).forEach(sections => {
      Object.values(sections).forEach(arr => {
        data.investigation_results.push(...arr)
      })
    })

    data.investigation_results = groupBy(
        data.investigation_results,
        'investigation_general_id'
    )

    return data;
}

const onClickToClose = () => {
    const params = Object.assign({}, route.params)
    params.billId = route.params.saleId;

    router.push({
        name: 'pending-sample-reports',
        params: params,
        query: route.query
    })
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}&is_edit=true`;
    if(route.params.type) query += `&investigation_type=` + replaceDashWithUnderscore(route.params.type);
    if(route.query.investigation_ids) query += `&investigation_ids=${decodeURIComponent(route.query.investigation_ids)}`;
    if(route.query.report_type) query += `&report_type=` + route.query.report_type;
    return query;
}

const fetchInitialData = async () => {
    const saleId = route.params.saleId;
    const query = getQuery();

    loader.value = true;

    await fetchSaleGeneral(query, saleId)
        .then((res) => {
            if (!res.status) return
            reports.value = res.data;
            setInvestigationData();
        }).catch((err) => {
            console.log(err)
        });

    loader.value = false
}

const setInvestigationData = () => {
    if (reports.value.approved_by_resource && reports.value.approved_by_resource.id) {
      approvedBy.value = reports.value.approved_by_resource;
    }
    if (reports.value.prepared_by_resource && reports.value.prepared_by_resource.id) {
      preparedBy.value = reports.value.prepared_by_resource;
    }
    deliveryDate.value = reports.value.expected_delivery_date ? reports.value.expected_delivery_date : date.currentDate();
    formatLeftSideData(reports.value.contact_profile, reports.value)
    setRightSideData(reports.value);
    attachments.value = reports.value.investigation_attachments;
    note.value = reports.value.note ? reports.value.note : '';
}

const getAge = (birthday, reportDate) => {
    if(!birthday) return '';
    return calculateAge(birthday, reportDate);
}

const formatLeftSideData = (patientInfo, reports) => {
    const age = getAge(patientInfo.birthday, reports.date);

    tableHeaderData.leftSideData = [
        { key: 'Patient ID', value: patientInfo.serial_no },
        { key: 'Patient Name', value: patientInfo.full_name },
        { key: 'Sex & Age', value: patientInfo.gender + ' | ' + age},
        { key: 'Ref By', value: reports.service_resource.name },
    ];
}

const setRightSideData = (reports) => {
    tableHeaderData.rightSideData = [
        { key: 'Invoice no', value: reports.bill_number },
        { key: 'Invoice date', value: formatDateToDdMmYy(reports.date) },
        { key: 'Investigation SL No', value: ''},
    ]
}

const goToPrint = async (isHideHeaderFooter) => {
    try {
        await Promise.all([
            fetchInitialData(),
            getCompanyInfo(),
        ]);

        generatePdf(companyInfo.value, {
          ...reports.value,
          formattedGenerals: getPrintableGenerals(),
        }, isHideHeaderFooter);
    } catch (err) {
        console.log(err);
    }
};

const generatePdf = async (company, data, isHideHeaderFooter) => {
    const user = store.state.user.user;
    const isHideFooter = isHideHeaderFooter;
    const isHideHeader = isHideHeaderFooter;
    const isDownload = false;

    const firstProductKey = Object.keys(data.formattedGenerals)[0];
    const firstReportCategory = Object.keys(data.formattedGenerals[firstProductKey])[0];
    const firstInvestigation = data.formattedGenerals[firstProductKey][firstReportCategory][0];
    const reportName = firstInvestigation.head_name;

    let qrcodeData = JSON.stringify({
        ref_no: data.id,
        ref_date: data.date,
        created_by: user.name,
        created_date_time: formatDate(new Date())
    })
    const barCodeQuery = `?code=${data.bill_number}&qrcode=${qrcodeData}&height=20&width=1`;
    const barCodePatientQuery = `?code=${data.contact_profile.serial_no}&height=20`;
    await Promise.all([
        fetchBarcode(barCodeQuery).then((res) => {
            barcode.value = res.barcode;
            qrcode.value = res.qrcode;
        }),
        fetchBarcode(barCodePatientQuery).then((res) => {
            barcodePatient.value = res.barcode;
        })
    ]).then(() => {
        exportToPDF(
            company,
            data,
            barcode.value,
            qrcode.value,
            barcodePatient.value,
            user.name,
            convertToTitleCase(reportName),
            isHideHeader,
            isHideFooter,
            isDownload,
            customizationPdfFields.value
        )
    }).catch((e) => {
        console.log('Something went wrong');
    })
}

const getPrintableGenerals = () => {
  return Object.keys(selectedGenerals.value).length > 0
    ? selectedGenerals.value
    : reports.value.formattedGenerals;
};

const getCompanyInfo = async () => {
    const companyId = route.params.companyId;
    await fetchCompanyInfo(companyId)
        .then((res) => {
            if(res.status){
                companyInfo.value = res.data;
            }
        }).catch((error) => {
            console.log(error);
        });
}

const updateStatus = async () => {
    if(! confirm('Are you sure you want to update status?')) return;

    loadingStatus.value = true;

    const dataRes = await updateInvestigationGeneral(formatFormData());

    if(! dataRes.status) {
        loadingStatus.value = false;
        return showError(dataRes.message);
    }

    await saveAttachment();

    const payload = {
        investigation_ids: decodeURIComponent(route.query.investigation_ids),
        status: 'approved',
        company_id: route.params.companyId,
    };

    const statusRes = await updateInvestigationStatus(payload);

    if(! statusRes.status) {
        loadingStatus.value = false;
        return showError(statusRes.message);
    }

    showSuccess(statusRes.message);
    loadingStatus.value = false;
    onClickToClose();
}

const getAntibioticDropdown = async () => {
    antibioticsDrowdown.value = [];
    try {
        const res = await fetchHome();
        if(res.status) {
            Object.keys(res.data.antibiotics_dropdown).forEach((val)=>{
                antibioticsDrowdown.value.push({
                    id: val,
                    name: res.data.antibiotics_dropdown[val]
                });
            });
        }
    } catch (err) {
        console.log(err);
    }
}

const getActiveFiledCustomization = async () => {
    const companyQuery = `?company_id=${route.params.companyId}&customization_type=pdf&type=investigation_report_pdf`;
    try {
        const res = await fetchActiveCustomizationField(companyQuery);
        customizationPdfFields.value = res.data;
    } catch (error) {
        console.error("Error fetching customization fields:", error);
    }
}

onMounted( async () => {
    deliveryDate.value = date.currentDate();
    await getAntibioticDropdown();
    await fetchInitialData();
    await getActiveFiledCustomization();
})
</script>

<style scoped>
.dropdown-consultant {
    width: 100% !important;
}
.table-divider{
    padding: 3px 10px;
}
.table-divider:after {
    content: ":";
}
.tr-box-shadow{
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px !important;
}
.width-10{
    width: 10%;
}
address{
    margin-bottom: 0 !important;
}
</style>